<template>
  <div class="p-4">
    <!-- Кнопка "Создать спецификацию" и чекбокс удалённых -->
    <div class="mb-4 flex items-center gap-4">
      <button v-can-write @click="openModal"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
        Создать спецификацию
      </button>
      <label class="flex items-center gap-2 cursor-pointer">
        <input v-model="showTrashed" type="checkbox" class="rounded border-gray-300" @change="onShowTrashedChange" />
        <span class="text-sm text-gray-700">Показать удалённые</span>
      </label>
    </div>

    <!-- Состояние загрузки -->
    <div v-if="loading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- Таблица технических карт -->
    <table v-else class="w-full border-collapse bg-white shadow-md">
      <thead class="bg-gray-100">
      <tr>
        <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">#</th>
        <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Название</th>
        <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Продукция</th>
        <th class="px-4 py-2 text-left text-sm font-medium text-gray-700"></th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="techMap in techMaps" :key="techMap.id"
          :class="['border-b', showTrashed ? 'opacity-75 bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50']">
        <td class="px-4 py-2 text-sm text-gray-700">{{ techMap.id }}</td>
        <td class="px-4 py-2">
          <div class="text-sm text-gray-700">{{ techMap.name }}</div>
          <div class="text-xs text-gray-500 mt-1">{{ techMap.description }}</div>
        </td>
        <td class="px-4 py-2 text-sm text-gray-700">
          <span :class="{ 'line-through text-gray-500': techMap.product?.deleted_at }">
            {{ techMap.product?.sku ? techMap.product.sku + ' - ' + techMap.product.name : (techMap.product?.name || 'Не указана') }}
          </span>
          <span v-if="techMap.product?.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
        </td>
        <td class="px-4 py-2">
          <div class="flex justify-end space-x-2">
            <template v-if="showTrashed">
              <button
                v-can-write
                @click="confirmRestore(techMap.id)"
                class="px-2 py-1 text-sm font-medium text-green-600 bg-green-100 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Восстановить
              </button>
            </template>
            <template v-else>
              <button
                v-can-write
                @click="$router.push({ name: 'TechMapEdit', params: { id: techMap.id }, query: { ...$route.query } })"
                class="ml-2 px-2 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Редактировать
              </button>
              <button
                v-can-write
                @click="openCopyModal(techMap.id)"
                class="ml-2 px-2 py-1 text-sm font-medium text-green-600 bg-green-100 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Копировать
              </button>
              <button
                v-can-write
                @click="confirmDelete(techMap.id)"
                class="px-2 py-1 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Удалить
              </button>
            </template>
          </div>
        </td>
        </tr>
      </tbody>
    </table>

    <Pagination
      v-if="pagination.lastPage > 1"
      :current-page="pagination.currentPage"
      :last-page="pagination.lastPage"
      :total="pagination.total"
      :per-page="pagination.perPage"
      @change-page="changePage"
    />

    <ConfirmModal
      v-if="showRestoreModal"
      :show="showRestoreModal"
      title="Восстановление спецификации"
      message="Вы уверены, что хотите восстановить эту техническую карту?"
      confirm-text="Восстановить"
      confirm-variant="success"
      @confirm="proceedRestore"
      @cancel="cancelRestore"
    />

    <!-- Модальное окно подтверждения удаления -->
    <div v-if="isConfirmModalVisible"
         class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">Подтвердите удаление</h2>
        <p class="text-sm text-gray-700 mb-4">Вы уверены, что хотите удалить эту техническую карту?</p>
        <div class="flex justify-end space-x-2">
          <button
            @click="cancelDelete"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Отмена
          </button>
          <button
            @click="proceedDelete"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Удалить
          </button>
        </div>
      </div>
    </div>

    <!-- Модальное окно создания спецификации -->
    <CreateTechMapModal :is-visible="isCreateModalVisible" @close="closeModal" @tech-map-created="fetchTechMaps"/>

    <!-- Модальное окно копирования спецификации -->
    <div v-if="isCopyModalVisible"
         class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-lg p-6">
        <h2 class="text-lg font-bold mb-4">Копировать спецификацию</h2>
        
        <!-- Выбор продукции -->
        <div class="mb-4">
          <label for="product-select" class="block text-sm font-medium text-gray-700 mb-2">
            Выберите продукцию для новой спецификации:
          </label>
          <v-select
            v-model="selectedProductId"
            :reduce="product => product"
            :options="sortedProducts"
            :filterable="false"
            label="name"
            placeholder="Начните вводить название или артикул..."
            :loading="loadingProducts"
            @search="onProductSearch"
            class="w-full"
          >
            <template #option="option">
              <span>{{ option.sku ? option.sku + ' - ' + option.name : option.name }}</span>
            </template>
            <template #selected-option="option">
              <span>{{ option.sku ? option.sku + ' - ' + option.name : option.name }}</span>
            </template>
            <template #search="{attributes, events}">
              <input
                class="vs__search"
                :required="!selectedProductId"
                v-bind="attributes"
                v-on="events"
              />
            </template>
            <template #no-options="{ search, searching, loading }">
              <span v-if="loadingProducts">Загружается...</span>
              <span v-else>Продукция не найдена</span>
            </template>
          </v-select>
        </div>

        <!-- Название новой спецификации -->
        <div class="mb-4">
          <label for="copy-name" class="block text-sm font-medium text-gray-700 mb-2">
            Название новой спецификации:
          </label>
          <input
            id="copy-name"
            v-model="copyTechMapName"
            type="text"
            placeholder="Введите название..."
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <!-- Состояние загрузки копирования -->
        <div v-if="copyLoading" class="flex justify-center items-center mb-4">
          <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
          <span class="ml-2 text-sm text-gray-600">Копирование...</span>
        </div>

        <div class="flex justify-end space-x-2">
          <button
            @click="closeCopyModal"
            :disabled="copyLoading"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            @click="proceedCopy"
            :disabled="!selectedProductId || !copyTechMapName.trim() || copyLoading"
            class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Копировать
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CreateTechMapModal from './CreateTechMapModal.vue';
import Pagination from '@/components/Pagination.vue';
import ConfirmModal from '@/components/common/ConfirmModal.vue';
import vSelect from 'vue-select';
import { createUrlStateManager } from '@/utils/urlStateManager';

export default {
  components: {
    CreateTechMapModal,
    Pagination,
    ConfirmModal,
    vSelect
  },
  data() {
    return {
      techMaps: [], // Список технических карт
      loading: true, // Флаг загрузки
      isConfirmModalVisible: false,
      techMapIdToDelete: null,
      showRestoreModal: false,
      techMapIdToRestore: null,
      isCreateModalVisible: false, // Флаг видимости модального окна создания
      isCopyModalVisible: false, // Флаг видимости модального окна копирования
      techMapIdToCopy: null, // ID технической карты для копирования
      products: [], // Список продукции для выбора
      selectedProductId: '', // Выбранная продукция
      copyTechMapName: '', // Название новой спецификации
      copyLoading: false, // Флаг загрузки копирования
      loadingProducts: false,
      productSearchTimer: null,
      page: 1,
      perPage: 20,
      showTrashed: false,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        perPage: 20,
      },
    };
  },
  computed: {
    sortedProducts() {
      return [...this.products].sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  created() {
    createUrlStateManager(this, {
      page: { type: 'number', default: 1, urlKey: 'page' },
      showTrashed: { type: 'boolean', default: false, urlKey: 'trashed' },
    }, {
      routeName: 'Production',
      additionalQuery: { tab: 'specifications' }
    });

    this.fetchTechMaps();
  },
  methods: {
    onShowTrashedChange() {
      this.techMaps = [];
      this.page = 1;
      const query = { ...this.$route.query };
      delete query.page;
      query.trashed = this.showTrashed;
      this.$router.replace({ name: this.$route.name, params: this.$route.params, query }).catch(() => {});
      this.fetchTechMaps();
    },
    async fetchTechMaps() {
      try {
        this.loading = true;
        const response = await axios.post('/api/techMap/getTechMaps', {
          page: this.page,
          per_page: this.perPage,
          search: this.$route.query.search,
          only_trashed: this.showTrashed,
        });
        const d = response.data.data;
        this.techMaps = d.data || [];
        this.pagination = {
          currentPage: d.current_page,
          lastPage: d.last_page,
          total: d.total,
          perPage: d.per_page,
        };
      } catch (error) {
        console.error('Ошибка при загрузке спецификаций:', error);
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      this.page = page;
      this.fetchTechMaps();
    },

    confirmDelete(id) {
      this.techMapIdToDelete = id; // Сохраняем ID технической карты
      this.isConfirmModalVisible = true; // Показываем модальное окно
    },

    cancelDelete() {
      this.isConfirmModalVisible = false; // Скрываем модальное окно
      this.techMapIdToDelete = null; // Очищаем ID
    },

    async proceedDelete() {
      if (!this.techMapIdToDelete) {
        console.error('ID технической карты не указан');
        return;
      }

      try {
        await axios.post('/api/techMap/deleteTechMap', {id: this.techMapIdToDelete});
        this.fetchTechMaps(); // Обновляем список технических карт
        this.cancelDelete(); // Скрываем модальное окно
      } catch (error) {
        console.error('Ошибка при удалении технической карты:', error);
      }
    },
    openModal() {
      this.isCreateModalVisible = true;
    },
    closeModal() {
      this.isCreateModalVisible = false;
    },

    openCopyModal(techMapId) {
      this.techMapIdToCopy = techMapId;
      this.isCopyModalVisible = true;
      this.products = [];
      this.selectedProductId = '';
      this.onProductSearch('');
    },

    closeCopyModal() {
      this.isCopyModalVisible = false;
      this.techMapIdToCopy = null;
      this.selectedProductId = '';
      this.copyTechMapName = '';
      this.copyLoading = false;
    },

    onProductSearch(search) {
      clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(async () => {
        this.loadingProducts = true;
        try {
          const response = await axios.post('/api/product/getProducts', {
            page: 1,
            per_page: 50,
            search: search?.trim() || ''
          });
          const d = response.data.data;
          this.products = d.data || [];
        } catch (error) {
          console.error('Ошибка при загрузке продукции:', error);
          this.products = [];
        } finally {
          this.loadingProducts = false;
        }
      }, 350);
    },

    confirmRestore(id) {
      this.techMapIdToRestore = id;
      this.showRestoreModal = true;
    },
    cancelRestore() {
      this.showRestoreModal = false;
      this.techMapIdToRestore = null;
    },
    async proceedRestore() {
      if (!this.techMapIdToRestore) return;
      try {
        await axios.post('/api/techMap/restoreTechMap', { id: this.techMapIdToRestore });
        await this.fetchTechMaps();
      } catch (error) {
        console.error('Ошибка при восстановлении спецификации:', error);
      } finally {
        this.cancelRestore();
      }
    },

    async proceedCopy() {
      if (!this.techMapIdToCopy || !this.selectedProductId || !this.copyTechMapName.trim()) {
        console.error('Не все данные заполнены для копирования');
        return;
      }

      this.copyLoading = true;

      try {
        await axios.post('/api/techMap/copyTechMap', {
          source_tech_map_id: this.techMapIdToCopy,
          target_product_id: this.selectedProductId?.id,
          name: this.copyTechMapName.trim()
        });

        // Обновляем список технических карт
        await this.fetchTechMaps();
        
        // Закрываем модальное окно
        this.closeCopyModal();
        
        console.log('Спецификация успешно скопирована');
      } catch (error) {
        console.error('Ошибка при копировании спецификации:', error);
      } finally {
        this.copyLoading = false;
      }
    }
  },
};
</script>
